<template>
  <div class="participant-swaps" v-if="!isCurrentPageRootLevel">
    <v-overlay :value="swapsLoading" z-index="4">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <div class="swaps-header mb-12">
      <div class="filters">
        <date-range-calendars-picker
          :mode="'select'"
          :initialEndDate="selectedRange.endDate"
          :initialStartDate="selectedRange.startDate"
          :limitSelectedDays="365"
          @select-range="selectedDate"
          :can-be-empty="true"
        />
        <dialog-select
          name="status"
          :items="statusValues"
          :value="status"
          placeholder="status"
          customClass="filter ml-4"
          itemText="label"
          itemValue="value"
          :clearable="true"
          @input="changeStatus($event.value)"
        />
        <v-btn
          v-if="displayType === 'RISE_MAP'"
          rounded
          icon
          outlined
          @click="generateRiseMapPdf"
          color="accent"
          class="ml-4"
          :loading="generatePdfLoading"
        >
          <v-icon>bhi-download</v-icon>
        </v-btn>

        <v-menu rounded v-if="displayType === 'RISE_MAP'" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              rounded
              outlined
              color="accent"
              class="ml-4"
              :loading="generateTemplatePdfLoading"
              v-bind="attrs"
              v-on="on"
            >
              Download Template <v-icon class="ml-2">bhi-download</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item link @click="generateRiseMapTemplatePdf('en')">
              English
            </v-list-item>
            <v-list-item link @click="generateRiseMapTemplatePdf('es')">
              Spanish
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div class="display">
        <v-tabs class="tabs" v-model="displayType" :height="40" color="accent">
          <v-tabs-slider></v-tabs-slider>
          <v-tab tab-value="LIST" href="#LIST">List</v-tab>
          <v-tab tab-value="RISE_MAP" href="#RISE_MAP">Rise Map</v-tab>
        </v-tabs>
      </div>
    </div>
    <div v-if="swaps && swaps.length">
      <div class="participant-swaps__list" v-if="displayType === 'LIST'">
        <main-user-table
          v-if="swaps && swaps.length"
          :headers="headers"
          :items="swaps"
          :items-per-page="-1"
          :show-all-items="true"
          :server-items-length="-1"
          :page="0"
          :row-component="'participant-swaps-table-row'"
          :hide-default-footer="true"
          @to-swap="navigateToSwap"
          @action:get-data="getDataAction"
        />
      </div>
      <div
        class="participant-swaps__rise-map"
        v-else-if="displayType === 'RISE_MAP'"
      >
        <div class="rise-map">
          <span class="participant-swaps__rise-map-label vertical">&nbsp;</span>
          <span class="participant-swaps__rise-map-label">EASY</span>
          <span class="participant-swaps__rise-map-label">HARD</span>
          <span class="participant-swaps__rise-map-label vertical">MAJOR</span>
          <div class="participant-swaps__rise-map-swaps gray">
            <participant-swaps-rise-map-item
              v-for="swap in easyMajorSwaps"
              :swap="swap"
              @to-swap="navigateToSwap"
              :key="`swap-rise-map-item-${swap.id}`"
            />
          </div>
          <div class="participant-swaps__rise-map-swaps">
            <participant-swaps-rise-map-item
              v-for="swap in hardMajorSwaps"
              :swap="swap"
              @to-swap="navigateToSwap"
              :key="`swap-rise-map-item-${swap.id}`"
            />
          </div>
          <span class="participant-swaps__rise-map-label vertical">MINOR</span>
          <div class="participant-swaps__rise-map-swaps">
            <participant-swaps-rise-map-item
              v-for="swap in easyMinorSwaps"
              :swap="swap"
              @to-swap="navigateToSwap"
              :key="`swap-rise-map-item-${swap.id}`"
            />
          </div>
          <div class="participant-swaps__rise-map-swaps gray">
            <participant-swaps-rise-map-item
              v-for="swap in hardMinorSwaps"
              :swap="swap"
              @to-swap="navigateToSwap"
              :key="`swap-rise-map-item-${swap.id}`"
            />
          </div>
        </div>
      </div>
    </div>
    <v-container v-else>
      <v-row class="fill-height">
        <v-col>
          <div
            class="d-flex flex-column align-center justify-center text-center pa-6 ma-6"
          >
            <h4 class="mb-4">{{ tenantTexts.participantSwaps.notFound }}</h4>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-btn
      elevation="2"
      fab
      bottom
      right
      @click="openSwapDialog"
      color="accent"
      class="participant-swaps__fab"
    >
      <v-icon>bhi-plus</v-icon>
    </v-btn>
    <swap-dialog
      @on-submit="prepareAddSwap"
      :show-dialog="showAddSwapDialog"
      :submit-loading-refresh-key="submitLoadingRefreshKey"
      :key="addSwapDialogKey"
      @close-swap-dialog="closeSwapDialog"
    />
  </div>
  <router-view v-else />
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import DateRangeCalendarsPicker from '@/components/inputs/DateRangeCalendarsPicker.vue'
import RouterMixin from '@/mixins/router'
import TenantTextsMixin from '@/mixins/tenantTexts'
import MainUserTable from '@/components/tables/MainUserTable.vue'
import ParticipantSwapsRiseMapItem from '@/components/swaps/ParticipantSwapsRiseMapItem.vue'
import SwapDialog from '@/components/dialogs/SwapDialog.vue'
import DialogSelect from '@/components/forms/DialogSelect'

// @ is an alias to /src
export default {
  name: 'ParticipantSwaps',
  components: {
    MainUserTable,
    ParticipantSwapsRiseMapItem,
    SwapDialog,
    DateRangeCalendarsPicker,
    DialogSelect,
  },
  mixins: [TenantTextsMixin, RouterMixin],
  data: () => ({
    displayType: 'LIST',
    headers: [
      { text: 'Swapping to & from', value: 'goal', sortable: false },
      { text: 'Category', value: 'impactLevel', sortable: false },
      { text: 'Status', value: 'status', sortable: false },
      { text: 'Update', value: 'lastModifiedDate', sortable: false },
      { text: 'Planned', value: 'repeatGoal', sortable: false },
      { text: 'Completed', value: 'repeatState', sortable: false },
      { text: 'Progress', value: 'repeatState', sortable: false },
      { sortable: false },
    ],
    showAddSwapDialog: false,
    addSwapDialogKey: 99,
    submitLoadingRefreshKey: 0,
    selectedRange: {},
    status: null,
    generatePdfLoading: false,
    generateTemplatePdfLoading: false,
  }),
  computed: {
    ...mapState('patientsStates', ['currentPatient', 'swapsLoading', 'swaps']),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
    ]),
    isCurrentPageRootLevel() {
      return this.$route.meta.root
    },
    currentParticipantId() {
      return this.$route.params?.id || this.currentPatient?.id || null
    },
    easyMajorSwaps() {
      return (this.swaps || []).filter(
        el => el.difficultLevel === 'EASY' && el.impactLevel === 'MAJOR',
      )
    },
    hardMajorSwaps() {
      return (this.swaps || []).filter(
        el => el.difficultLevel === 'HARD' && el.impactLevel === 'MAJOR',
      )
    },
    easyMinorSwaps() {
      return (this.swaps || []).filter(
        el => el.difficultLevel === 'EASY' && el.impactLevel === 'MINOR',
      )
    },
    hardMinorSwaps() {
      return (this.swaps || []).filter(
        el => el.difficultLevel === 'HARD' && el.impactLevel === 'MINOR',
      )
    },
    statusValues() {
      return [
        {
          label: 'New',
          value: 'NEW',
        },
        {
          label: 'In progress',
          value: 'IN_PROGRESS',
        },
        {
          label: 'Claimed',
          value: 'DONE',
        },
        {
          label: 'Rejected',
          value: 'REJECTED',
        },
      ]
    },
  },
  mounted() {
    this.init()
  },
  created() {},
  watch: {
    currentPatient(patient) {
      if (patient?.id) this.init()
    },
    $route: {
      handler(to, from) {
        // if participant id changed
        if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
          this.getPatient(to.params.id)
        }
        // if moved to participant swaps
        if (
          from?.name &&
          from?.name !== 'Participant Swaps' &&
          to?.name &&
          to?.name === 'Participant Swaps'
        ) {
          this.init()
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions('patientsStates', [
      'getPatient',
      'getSwaps',
      'generateRiseMap',
      'addSwap',
    ]),
    init() {
      if (!this.isCurrentPageRootLevel) {
        this.getSwaps({ participantId: this.currentParticipantId })
      }
    },
    navigateToSwap(swapId) {
      this.$router.push({
        name: 'Participant Swap',
        path: '/participants/:id/swaps/:swapid',
        params: {
          id: this.currentParticipantId,
          swapid: swapId,
        },
      })
    },
    openSwapDialog() {
      this.showAddSwapDialog = true
      this.submitLoadingRefreshKey++
    },
    closeSwapDialog() {
      this.showAddSwapDialog = false
      this.addSwapDialogKey++
    },
    async prepareAddSwap(swapData) {
      const response = await this.addSwap({
        participantId: this.currentParticipantId,
        data: swapData,
      })
      this.submitLoadingRefreshKey++
      if ([200, 201].includes(response?.status)) {
        this.closeSwapDialog()
        this.getSwaps({
          participantId: this.currentParticipantId,
        })
      }
    },
    selectedDate(range) {
      this.selectedRange.startDate = range.startDate
      this.selectedRange.endDate = range.endDate

      this.searchWithParameters()
    },
    changeStatus(val) {
      this.status = val
      this.searchWithParameters()
    },
    searchWithParameters() {
      const startDate = this.selectedRange.startDate
        ? this.$moment(this.selectedRange.startDate).toISOString()
        : null
      const endDate = this.selectedRange.endDate
        ? this.$moment(this.selectedRange.endDate).toISOString()
        : null
      this.getSwaps({
        participantId: this.currentParticipantId,
        from: startDate,
        to: endDate,
        status: this.status,
      })
    },
    async generateRiseMapTemplatePdf(language = 'en') {
      if (this.generateTemplatePdfLoading === true) {
        return
      }
      this.generateTemplatePdfLoading = true

      const responseData = await this.generateRiseMap({
        participantId: this.currentParticipantId,
        language,
        empty: true,
      })

      if (responseData) {
        const generatedTime = this.$moment().format('YYYY-MM-DD h-mm-ss a')
        const blob = new Blob([responseData], {
          type: 'application/pdf;',
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `GeneratedRiseMap-template-generated-${generatedTime}-${language}.pdf`
        link.click()
        URL.revokeObjectURL(link.href)
      }
      this.generateTemplatePdfLoading = false
    },
    async generateRiseMapPdf() {
      if (this.generatePdfLoading === true) {
        return
      }
      this.generatePdfLoading = true
      const startDate = this.selectedRange.startDate
        ? this.$moment(this.selectedRange.startDate).toISOString()
        : null
      const endDate = this.selectedRange.endDate
        ? this.$moment(this.selectedRange.endDate).toISOString()
        : null
      const responseData = await this.generateRiseMap({
        participantId: this.currentParticipantId,
        from: startDate,
        to: endDate,
        status: this.status,
      })
      if (responseData) {
        let datesName = 'all'
        const sName = this.$moment(startDate).format('YYYY-MM-DD')
        const eName = this.$moment(endDate).format('YYYY-MM-DD')
        if (startDate && endDate) {
          datesName = `${sName}-${eName}`
        } else if (startDate) {
          datesName = `from-${sName}`
        } else if (endDate) {
          datesName = `to-${eName}`
        }
        const generatedTime = this.$moment().format('YYYY-MM-DD h-mm-ss a')
        const blob = new Blob([responseData], {
          type: 'application/pdf;',
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `GeneratedRiseMap-${datesName}-generated-${generatedTime}.pdf`
        link.click()
        URL.revokeObjectURL(link.href)
      }
      this.generatePdfLoading = false
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.participant-swaps {
  flex: 1;
  padding: 24px 10px 0;
  height: 100%;

  .v-data-table-header th {
    border-bottom: none !important;
    padding: 0 8px ($gutter * 0.5) !important;

    &:first-of-type {
      padding-left: 24px !important;
    }
    &:last-of-type {
      padding-right: 24px !important;
    }
  }

  &__rise-map {
    .rise-map {
      display: flex;
      flex-wrap: wrap;
    }

    &-label {
      font-weight: 700;
      text-align: center;
      color: var(--bh-gray--2);
      &:not(.vertical) {
        width: calc(50% - 13px);
      }
      &.vertical {
        width: 26px;
        writing-mode: tb-rl;
        transform: rotate(180deg);
      }
    }

    &-swaps {
      width: calc(50% - 13px);
      min-height: 285px;
      height: 40vh;
      overflow-y: scroll;
      padding: 16px 24px;
      background-color: #ffffff;
      border-top: 8px solid #ffffff;
      border-bottom: 8px solid #ffffff;

      &.gray {
        background-color: var(--bh-gray--6);
        border-top-color: var(--bh-gray--6);
        border-bottom-color: var(--bh-gray--6);
      }
    }
  }

  .swaps-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    .filters {
      display: flex;
    }
    .display {
      border: 1px solid #ebf0ff;
      border-radius: 28px;
      padding: 3px;
    }
    .tabs {
      &.v-tabs {
        // width: 600px;
        margin: 0 auto;
        background: $bh-white;
        border-radius: 9999px;
        .v-tabs-slider-wrapper {
          height: 100% !important;
          border-radius: 999px !important;
          overflow: hidden;
          z-index: -1;
        }
        .v-slide-group__wrapper {
          background: $bh-white;
          border-radius: 999px;
        }
        & > .v-tabs-bar {
          .v-tab {
            transition-duration: 500ms !important;
            transition-property: color;
          }
          .v-tab.v-tab--active {
            color: rgba(255, 255, 255, 1);
          }
          .v-tab:hover::before {
            display: none !important;
          }
          background: $bh-blue--4 !important;
        }
      }
    }
  }

  &__fab {
    bottom: 0;
    right: 0;
    position: fixed;
    margin: 0 16px 16px 0;
  }
}
</style>
